import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Building2, MapPin, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';

export default function AdminBranches() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [saving, setSaving] = useState(false);
  const [empCounts, setEmpCounts] = useState({});

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const [branchRes, empRes] = await Promise.all([
        adminAPI.getBranches(),
        adminAPI.getEmployees(null, 1, 500)
      ]);
      setBranches(branchRes.data || []);
      const counts = {};
      (empRes.data?.employees || []).forEach(e => {
        const b = e.branch || 'Unassigned';
        counts[b] = (counts[b] || 0) + 1;
      });
      setEmpCounts(counts);
    } catch {
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Branch name is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await adminAPI.updateBranch(editId, { name: name.trim(), address, city });
        toast.success('Branch updated');
      } else {
        await adminAPI.createBranch({ name: name.trim(), address, city });
        toast.success('Branch created');
      }
      resetForm();
      fetchBranches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (b) => {
    setEditId(b.id);
    setName(b.name);
    setAddress(b.address || '');
    setCity(b.city || '');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this branch? Employees assigned here will be unassigned.')) return;
    try {
      await adminAPI.deleteBranch(id);
      toast.success('Branch deleted');
      fetchBranches();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditId(null);
    setName('');
    setAddress('');
    setCity('');
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 data-testid="branches-title" className="text-xl font-extrabold text-[#1a2529] tracking-tight">Branches</h1>
            <p className="text-sm text-[#1a2529]/50 mt-0.5">{branches.length} branch{branches.length !== 1 ? 'es' : ''}</p>
          </div>
          <Button data-testid="add-branch-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#026536] hover:bg-[#026536]/90 gap-1.5 font-semibold">
            <Plus size={16} /> Add Branch
          </Button>
        </div>

        {/* Form */}
        {showForm && (
          <div data-testid="branch-form" className="bg-white rounded-xl p-5 border border-border/40 shadow-sm animate-slide-up">
            <h3 className="font-bold text-sm text-[#1a2529] mb-4">{editId ? 'Edit Branch' : 'New Branch'}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Name *</Label>
                <Input data-testid="branch-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Mumbai Central" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">City</Label>
                <Input data-testid="branch-city-input" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g., Mumbai" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Address</Label>
                <Input data-testid="branch-address-input" value={address} onChange={e => setAddress(e.target.value)} placeholder="Full address" className="h-10 mt-1" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="save-branch-btn" onClick={handleSave} disabled={saving} className="bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-1.5">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                {editId ? 'Update' : 'Create'}
              </Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Branches List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : branches.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-border/40">
            <Building2 className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
            <p className="font-semibold text-[#1a2529]/50">No branches created</p>
            <p className="text-xs text-[#1a2529]/30 mt-1">Add your first branch to organize employees by location</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {branches.map(b => (
              <div key={b.id} data-testid={`branch-card-${b.id}`} className="bg-white rounded-xl p-4 border border-border/40 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="h-10 w-10 rounded-lg bg-[#026536]/10 flex items-center justify-center">
                      <Building2 size={18} className="text-[#026536]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#1a2529] text-sm">{b.name}</p>
                      {b.city && <p className="text-[11px] text-[#1a2529]/40">{b.city}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-branch-${b.id}`} onClick={() => handleEdit(b)} className="h-8 w-8 rounded-lg hover:bg-[#f4f4f5] flex items-center justify-center transition-colors">
                      <Pencil size={14} className="text-[#1a2529]/40" />
                    </button>
                    <button data-testid={`delete-branch-${b.id}`} onClick={() => handleDelete(b.id)} className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center transition-colors">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
                {b.address && (
                  <div className="flex items-start gap-1.5 mb-2">
                    <MapPin size={12} className="text-[#1a2529]/30 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-[#1a2529]/40 line-clamp-2">{b.address}</p>
                  </div>
                )}
                <Badge className="bg-[#026536]/10 text-[#026536] border-0 text-[10px]">
                  {empCounts[b.name] || 0} employees
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
